/**
 * Uptime (FOR-303): shapes and pure rules shared by Admin → Uptime and a
 * site's Uptime page. No React, no API client, so tests/uptime.test.mjs can
 * pin them. The API is docs/uptime.md in Gluska-Forge-API.
 */

export type MonitorStatus = "unknown" | "up" | "degraded" | "down" | "paused"
export type OverallStatus = "operational" | "degraded" | "partial" | "major" | "unknown"
export type ErrorClass = "timeout" | "dns" | "tls" | "connect" | "status" | "keyword" | "heartbeat" | "content"

export interface StripIncident {
  id: number
  cause: string | null
  started_at: string
  resolved_at: string | null
  duration_seconds: number
  /** "uptimerobot" for history imported from UptimeRobot. */
  source?: "forge" | "uptimerobot"
}

export interface StripDay {
  date: string
  /** Null = no data that day (grey). */
  uptime: number | null
  downtime_seconds: number
  incidents: StripIncident[]
  /** Whose measurement the day is: Forge's own, or imported from UptimeRobot (before Forge watched it). */
  source?: "forge" | "uptimerobot" | null
}

export interface UptimeProbe {
  id: number
  name: string
  region: string
  is_primary: boolean
  enabled: boolean
  has_token: boolean
  last_seen_at: string | null
  silent: boolean
  last_version: string | null
  checks_last_hour?: number
  failures_last_hour?: number
}

export interface OverviewRow {
  id: number
  name: string
  type: "http" | "heartbeat"
  url: string | null
  site_id: number | null
  site_domain: string | null
  status: MonitorStatus
  /** Why it is degraded ("Slow from ohio, miami (6.2 s, threshold 5.0 s)"). */
  status_detail?: string | null
  in_maintenance: boolean
  interval_minutes: number
  uptime_90d: number | null
  strip: StripDay[]
  last_response_ms: number | null
  last_checked_at: string | null
  last_ping_at: string | null
  sparkline: (number | null)[]
  ssl_days_left: number | null
  open_incident: { id: number; started_at: string; cause: string | null } | null
  /**
   * The WordPress origin companion of a client site ("<Site> (origin)",
   * /wp-json/forge/v1, uncached), folded into its site's row by the API. It
   * keeps checking and alerting on its own; only the display is one row.
   * Absent for a monitor without one.
   */
  origin?: OriginCheck | null
}

/** A site's second check, the one a page cache cannot hide (docs/uptime.md, "Origin check"). */
export interface OriginCheck {
  id: number
  name: string
  url: string | null
  status: MonitorStatus
  status_detail?: string | null
  in_maintenance: boolean
  last_response_ms: number | null
  last_checked_at: string | null
  uptime_90d: number | null
  strip: StripDay[]
  open_incident: { id: number; started_at: string; cause: string | null } | null
}

export interface UptimeOverview {
  overall: { status: OverallStatus; total: number; down: number; degraded: number; paused: number; maintenance: number }
  groups: { name: string; monitors: OverviewRow[] }[]
  probes: UptimeProbe[]
  primary_last_run_at: string | null
  open_incidents: number
  global_maintenance: boolean
  generated_at: string
}

export interface UptimeMonitor {
  id: number
  name: string
  type: "http" | "heartbeat"
  url: string | null
  method: "GET" | "HEAD" | "POST"
  expected_statuses: string
  keyword: string | null
  keyword_mode: "present" | "absent" | null
  follow_redirects: boolean
  timeout_seconds: number
  interval_minutes: number
  header_names: string[]
  regions: string[] | null
  effective_regions: string[]
  group: string
  site_id: number | null
  site_domain: string | null
  sort_order: number
  enabled: boolean
  paused_until: string | null
  alert_contacts: string[]
  alerts_enabled: boolean
  effective_contacts: string[]
  reminder_hours: number | null
  is_public: boolean
  ssl_check: boolean
  ping_url: string | null
  heartbeat_grace_minutes: number
  last_ping_at: string | null
  status: MonitorStatus
  status_changed_at: string | null
  last_checked_at: string | null
  last_response_ms: number | null
  ssl: { expires_at: string | null; days_left: number | null; issuer: string | null; error: string | null; checked_at: string | null } | null
  created_at: string | null
  /** Own slow threshold in ms; null = the default for its kind (effective_slow_ms). */
  slow_ms?: number | null
  effective_slow_ms?: number | null
  status_detail?: string | null
}

export interface UptimeCheck {
  id: number
  region: string
  ok: boolean
  status_code: number | null
  response_ms: number | null
  error_class: ErrorClass | null
  error: string | null
  checked_at: string
  slow?: boolean
  warning?: string | null
  attempts?: number
}

export interface UptimeIncident {
  id: number
  monitor_id: number
  monitor_name: string | null
  started_at: string
  resolved_at: string | null
  duration_seconds: number
  cause: string | null
  error_class: ErrorClass | null
  first_error: string | null
  regions: string[]
  acknowledged_at: string | null
  acknowledged_by: { id: number; name: string } | null
  notes: string | null
  alerted: boolean
  /** "uptimerobot" = imported history, never alerted. */
  source?: "forge" | "uptimerobot"
}

export type SeriesRange = "24h" | "7d" | "30d" | "90d"

export interface UptimeSeries {
  range: SeriesRange
  bucket_seconds: number
  regions: string[]
  points: ({ t: string } & Record<string, number | string>)[]
}

export interface UptimeWindow {
  id: number
  monitor_id: number | null
  starts_at: string
  ends_at: string
  note: string | null
  active: boolean
}

export interface MonitorDetail {
  monitor: UptimeMonitor
  /** On a site's page check: its WordPress origin companion. */
  origin?: OriginCheck | null
  /** On an origin companion: the site check it belongs to. */
  parent?: { id: number; name: string; status: MonitorStatus; in_maintenance: boolean } | null
  uptime: Record<SeriesRange, number | null>
  strip: StripDay[]
  series: UptimeSeries
  recent: RecentSeries
  incidents: UptimeIncident[]
  recent_checks: UptimeCheck[]
  regions: { region: string; probe: UptimeProbe | null; latest: UptimeCheck | null }[]
  maintenance: UptimeWindow[]
}

export interface SiteUptimeMonitor {
  id: number
  name: string
  url: string | null
  status: MonitorStatus
  in_maintenance: boolean
  interval_minutes: number
  last_checked_at: string | null
  last_response_ms: number | null
  uptime: Record<SeriesRange, number | null>
  strip: StripDay[]
  recent: RecentSeries
  regions: string[]
  ssl: { expires_at: string | null; days_left: number | null; issuer: string | null } | null
  incidents: { id: number; started_at: string; resolved_at: string | null; duration_seconds: number; cause: string | null; regions: string[] }[]
  /** The WordPress origin companion, folded in by the API. */
  origin?: SiteOriginCheck | null
}

export interface SiteOriginCheck {
  id: number
  name: string
  url: string | null
  status: MonitorStatus
  in_maintenance: boolean
  last_checked_at: string | null
  last_response_ms: number | null
  uptime: Record<SeriesRange, number | null>
  strip: StripDay[]
}

/** What the Add/Edit sheet sends. */
export interface MonitorInput {
  name: string
  type: "http" | "heartbeat"
  url?: string | null
  method?: string
  expected_statuses?: string
  keyword?: string | null
  keyword_mode?: "present" | "absent" | null
  follow_redirects?: boolean
  timeout_seconds?: number
  interval_minutes: number
  headers?: Record<string, string> | null
  regions?: string[] | null
  group?: string
  site_id?: number | null
  enabled?: boolean
  paused_until?: string | null
  alert_contacts?: string[] | null
  alerts_enabled?: boolean
  reminder_hours?: number | null
  ssl_check?: boolean
  heartbeat_grace_minutes?: number
  slow_ms?: number | null
}

// ---------------------------------------------------------------------------
// Rules
// ---------------------------------------------------------------------------

export type DayTone = "none" | "good" | "minor" | "major"

/**
 * One bar of the 90-day strip. 100% is green; down for under ~15 minutes
 * (99% or better) is amber; anything worse is red; no data is grey.
 */
export function dayTone(uptime: number | null): DayTone {
  if (uptime === null) return "none"
  if (uptime >= 99.995) return "good"
  if (uptime >= 99) return "minor"
  return "major"
}

export const DAY_TONE_CLASS: Record<DayTone, string> = {
  none: "bg-black/[0.08] dark:bg-white/[0.08]",
  good: "bg-[#30a46c] dark:bg-[#33b074]",
  minor: "bg-[#ffb224] dark:bg-[#f1a10d]",
  major: "bg-[#e5484d] dark:bg-[#e5484d]",
}

export const STATUS_LABEL: Record<MonitorStatus, string> = {
  up: "Up",
  degraded: "Degraded",
  down: "Down",
  unknown: "No data yet",
  paused: "Paused",
}

/** Dot colour for a monitor's current status. */
export const STATUS_DOT: Record<MonitorStatus, string> = {
  up: "bg-[#30a46c]",
  degraded: "bg-[#ffb224]",
  down: "bg-[#e5484d]",
  unknown: "bg-[#8b8d98]",
  paused: "bg-[#8b8d98]",
}

export function overallHeadline(status: OverallStatus): string {
  switch (status) {
    case "operational":
      return "All systems operational"
    case "degraded":
      return "Degraded performance"
    case "partial":
      return "Partial outage"
    case "major":
      return "Major outage"
    default:
      return "No monitors yet"
  }
}

/** "99.982%", "100%", or "-" when there is nothing to measure. */
export function formatUptime(value: number | null | undefined, digits = 2): string {
  if (value === null || value === undefined) return "-"
  if (value >= 100) return "100%"
  const floor = Math.floor(value * 10 ** digits) / 10 ** digits
  return `${floor.toFixed(digits)}%`
}

/** 45s, 12 min, 3 h 5 min, 2 d 4 h. */
export function formatDuration(seconds: number): string {
  if (seconds < 60) return `${Math.max(0, Math.round(seconds))}s`
  const minutes = Math.floor(seconds / 60)
  if (minutes < 60) return `${minutes} min`
  const hours = Math.floor(minutes / 60)
  const rest = minutes % 60
  if (hours < 24) return rest ? `${hours} h ${rest} min` : `${hours} h`
  const days = Math.floor(hours / 24)
  const h = hours % 24
  return h ? `${days} d ${h} h` : `${days} d`
}

export function relativeTime(iso: string | null, now: number = Date.now()): string {
  if (!iso) return "never"
  const s = Math.round((now - new Date(iso).getTime()) / 1000)
  if (s < 45) return "just now"
  const m = Math.round(s / 60)
  if (m < 60) return `${m} min ago`
  const h = Math.round(m / 60)
  if (h < 24) return `${h} h ago`
  return `${Math.round(h / 24)} d ago`
}

/** Short label for a strip day: "Oct 19". Dates are UTC days. */
export function dayLabel(date: string): string {
  const d = new Date(`${date}T00:00:00Z`)
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", timeZone: "UTC" })
}

export const REGION_LABEL: Record<string, string> = {
  ohio: "Ohio (Forge)",
  cloudflare: "Cloudflare",
  miami: "Miami",
  ping: "Ping",
  uptimerobot: "UptimeRobot",
}

export function regionLabel(region: string): string {
  return REGION_LABEL[region] ?? region
}

export const ERROR_CLASS_LABEL: Record<ErrorClass, string> = {
  timeout: "Timeout",
  dns: "DNS",
  tls: "TLS",
  connect: "Connection",
  status: "Status code",
  keyword: "Keyword",
  heartbeat: "Missed ping",
  content: "Broken page",
}

/** Monitors that need eyes first: down, then degraded, then the rest, keeping order. */
export function sortForAttention<T extends { status: MonitorStatus }>(rows: T[]): T[] {
  const rank: Record<MonitorStatus, number> = { down: 0, degraded: 1, unknown: 2, up: 3, paused: 4 }
  return rows.map((r, i) => [r, i] as const).sort((a, b) => rank[a[0].status] - rank[b[0].status] || a[1] - b[1]).map(([r]) => r)
}

// ---------------------------------------------------------------------------
// A site and its WordPress origin check, shown as one row
// ---------------------------------------------------------------------------

/**
 * Which status a combined row shows: a real reading beats "no data yet" and
 * "paused", so a companion that was only just added, or is paused, never
 * hides the other check's result.
 */
const COMBINE_RANK: Record<MonitorStatus, number> = { down: 0, degraded: 1, up: 2, unknown: 3, paused: 4 }

/** The worse of two statuses, by COMBINE_RANK. */
export function worseStatus(a: MonitorStatus, b: MonitorStatus | null | undefined): MonitorStatus {
  if (!b) return a
  return COMBINE_RANK[b] < COMBINE_RANK[a] ? b : a
}

interface CheckLike {
  status: MonitorStatus
  in_maintenance: boolean
  status_detail?: string | null
  open_incident?: { id: number; started_at: string; cause: string | null } | null
}

/**
 * What a row shows for a site with an origin companion: the worse of the two
 * checks wins the dot and the badge, and the open incident (and maintenance
 * flag and detail) come from whichever check that is. `which` says so.
 */
export function combinedState(row: CheckLike & { origin?: CheckLike | null }): {
  status: MonitorStatus
  in_maintenance: boolean
  status_detail: string | null
  open_incident: CheckLike["open_incident"]
  which: "site" | "origin"
} {
  const o = row.origin
  const originWorse = !!o && worseStatus(row.status, o.status) !== row.status
  const pick = originWorse && o ? o : row
  return {
    status: pick.status,
    in_maintenance: pick.in_maintenance,
    status_detail: pick.status_detail ?? null,
    // The site's own incident first; the origin's only when the site has none.
    open_incident: row.open_incident ?? o?.open_incident ?? null,
    which: originWorse ? "origin" : "site",
  }
}

/** "Site (cached) up · WordPress origin down", or null for a monitor without an origin check. */
export function originIndicator(row: { status: MonitorStatus; origin?: { status: MonitorStatus } | null }): string | null {
  if (!row.origin) return null
  const word = (s: MonitorStatus) => STATUS_LABEL[s].toLowerCase()
  return `Site (cached) ${word(row.status)} · WordPress origin ${word(row.origin.status)}`
}

/** Parse the sheet's "Name: value" header lines. Blank lines are skipped; a line with no colon is an error. */
export function parseHeaders(text: string): { headers: Record<string, string>; error: string | null } {
  const headers: Record<string, string> = {}
  for (const raw of text.split("\n")) {
    const line = raw.trim()
    if (!line) continue
    const at = line.indexOf(":")
    if (at <= 0) return { headers, error: `Header line "${line}" needs a name and a value` }
    const name = line.slice(0, at).trim()
    if (!/^[A-Za-z0-9-]{1,64}$/.test(name)) return { headers, error: `Header name "${name}" may use letters, digits and dashes` }
    headers[name] = line.slice(at + 1).trim()
  }
  return { headers, error: null }
}

// ---------------------------------------------------------------------------
// The fine-grained strip (24 hours in 5-minute buckets, 7 days in hours)
// ---------------------------------------------------------------------------

export type RecentRange = "24h" | "7d"
export type BucketStatus = "ok" | "partial" | "down" | "none" | "maintenance"

export interface RegionBucket {
  ok: number
  total: number
  avg_ms: number | null
  error_class: ErrorClass | null
}

export interface RecentBucket {
  /** ISO start of the bucket (UTC). */
  start: string
  status: BucketStatus
  ok: number
  total: number
  avg_ms: number | null
  regions: Record<string, RegionBucket>
}

export interface RecentSeries {
  range: RecentRange
  bucket_seconds: number
  buckets: RecentBucket[]
}

export const BUCKET_TONE_CLASS: Record<BucketStatus, string> = {
  ok: "bg-[#30a46c] dark:bg-[#33b074]",
  partial: "bg-[#ffb224] dark:bg-[#f1a10d]",
  down: "bg-[#e5484d] dark:bg-[#e5484d]",
  none: "bg-black/[0.08] dark:bg-white/[0.08]",
  maintenance: "bg-[#0090ff] dark:bg-[#3b9eff]",
}

export const BUCKET_LABEL: Record<BucketStatus, string> = {
  ok: "All checks passed",
  partial: "Some locations failed",
  down: "Down",
  none: "No checks",
  maintenance: "Maintenance",
}

const MERGE_RANK: BucketStatus[] = ["down", "partial", "maintenance", "ok", "none"]

/**
 * Join every `factor` neighbouring buckets into one (5-minute → 15-minute on
 * a phone, so each bar stays tappable). The worst status wins; counts add up;
 * response time is the check-weighted average.
 */
export function mergeBuckets(series: RecentSeries, factor: number): RecentSeries {
  if (factor <= 1) return series
  const out: RecentBucket[] = []
  // Group from the newest end so the last bar is always "now".
  const lead = series.buckets.length % factor
  for (let i = lead; i < series.buckets.length; i += factor) {
    const group = series.buckets.slice(i === lead ? 0 : i, i + factor)
    const status = MERGE_RANK.find((s) => group.some((b) => b.status === s)) ?? "none"
    const regions: Record<string, RegionBucket> = {}
    for (const b of group) {
      for (const [name, r] of Object.entries(b.regions)) {
        const prev = regions[name] ?? { ok: 0, total: 0, avg_ms: null, error_class: null }
        const timed = (prev.avg_ms === null ? 0 : prev.total) + (r.avg_ms === null ? 0 : r.total)
        regions[name] = {
          ok: prev.ok + r.ok,
          total: prev.total + r.total,
          avg_ms: timed ? Math.round(((prev.avg_ms ?? 0) * (prev.avg_ms === null ? 0 : prev.total) + (r.avg_ms ?? 0) * (r.avg_ms === null ? 0 : r.total)) / timed) : null,
          error_class: prev.error_class ?? r.error_class,
        }
      }
    }
    const total = group.reduce((n, b) => n + b.total, 0)
    const timed = group.filter((b) => b.avg_ms !== null)
    const timedTotal = timed.reduce((n, b) => n + b.total, 0)
    out.push({
      start: group[0].start,
      status,
      ok: group.reduce((n, b) => n + b.ok, 0),
      total,
      avg_ms: timedTotal ? Math.round(timed.reduce((n, b) => n + (b.avg_ms ?? 0) * b.total, 0) / timedTotal) : null,
      regions,
    })
  }
  return { ...series, bucket_seconds: series.bucket_seconds * factor, buckets: out }
}

/** "Oct 19, 11:30 to 11:35" in the viewer's time zone. */
export function bucketRangeLabel(start: string, seconds: number): string {
  const a = new Date(start)
  const b = new Date(a.getTime() + seconds * 1000)
  const day = a.toLocaleDateString("en-US", { month: "short", day: "numeric" })
  const t = (d: Date) => d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" })
  return `${day}, ${t(a)} to ${t(b)}`
}

/* ------------------------------------------------------------------ */
/* Pulse: every check in the last N minutes, drawn like a heart monitor. */

export type PulseMinutes = 15 | 60 | 180 | 360 | 720 | 1440

export interface PulseCheck {
  t: string
  region: string
  ok: boolean
  ms: number | null
  status_code: number | null
  error_class: string | null
  error: string | null
  slow: boolean
}

export interface PulseData {
  minutes: PulseMinutes
  from: string
  to: string
  interval_minutes: number
  regions: string[]
  checks: PulseCheck[]
}

export interface PulseBlip {
  x: number
  /** Checks folded into this beat when the window is too dense to draw each one. */
  count: number
  /** Spike height in px above the baseline (below it for a failed check). */
  h: number
  check: PulseCheck
}

export interface PulseTrace {
  /** The flat line with one QRS-style spike per passing check. */
  path: string
  /** One short downward stroke per failed check, drawn on its own in red. */
  failPath: string
  blips: PulseBlip[]
}

/**
 * Geometry for one lane of the pulse trace. A passing check is a spike whose
 * height is its response time against `scaleMs` (a heartbeat ping has no
 * time, so it gets a fixed beat); a failed check dips below the line instead.
 * Spikes narrow as checks crowd together so a busy day still reads as beats.
 */
export function pulseTrace(
  checks: PulseCheck[],
  fromMs: number,
  toMs: number,
  width: number,
  laneHeight: number,
  scaleMs: number,
  expectedCount: number,
): PulseTrace {
  const base = laneHeight - 6
  const span = Math.max(1, toMs - fromMs)
  // At most one beat every ~7 px: a denser window (a day of 1-minute checks)
  // folds neighbours into one beat, its slowest check, or a failure if any failed.
  const maxBeats = Math.max(1, Math.floor(width / 7))
  const beats = Math.min(Math.max(1, expectedCount), maxBeats)
  const spacing = width / beats
  const hw = Math.min(5, Math.max(1.2, spacing / 3))
  const tall = base - 4
  const sorted = foldChecks(
    [...checks].sort((a, b) => Date.parse(a.t) - Date.parse(b.t)),
    fromMs,
    span,
    expectedCount > maxBeats ? maxBeats : 0,
  )

  let path = `M0 ${base}`
  let failPath = ""
  const blips: PulseBlip[] = []
  for (const { check: c, count } of sorted) {
    const x = Math.round(((Date.parse(c.t) - fromMs) / span) * width * 10) / 10
    if (x < 0 || x > width) continue
    if (!c.ok) {
      const h = Math.min(6, laneHeight - base)
      failPath += `M${x - hw} ${base} L${x} ${base + h} L${x + hw} ${base} `
      blips.push({ x, h: -h, count, check: c })
      continue
    }
    const ratio = c.ms == null ? 0.6 : Math.min(1, c.ms / Math.max(1, scaleMs))
    const h = Math.max(4, Math.round(ratio * tall))
    path += ` L${round1(x - hw * 1.6)} ${base} L${round1(x - hw * 0.8)} ${base + 2} L${x} ${base - h} L${round1(x + hw * 0.8)} ${base + 3} L${round1(x + hw * 1.6)} ${base}`
    blips.push({ x, h, count, check: c })
  }
  path += ` L${width} ${base}`
  return { path, failPath: failPath.trim(), blips }
}

/** Groups checks into `buckets` equal time slots (0 = keep every check), each represented by its worst check. */
function foldChecks(checks: PulseCheck[], fromMs: number, span: number, buckets: number): { check: PulseCheck; count: number }[] {
  if (buckets === 0) return checks.map((check) => ({ check, count: 1 }))
  const slots = new Map<number, { check: PulseCheck; count: number; sumT: number }>()
  for (const c of checks) {
    const t = Date.parse(c.t)
    const i = Math.min(buckets - 1, Math.max(0, Math.floor(((t - fromMs) / span) * buckets)))
    const slot = slots.get(i)
    if (!slot) {
      slots.set(i, { check: c, count: 1, sumT: t })
      continue
    }
    slot.count++
    slot.sumT += t
    const worse = (!c.ok && slot.check.ok) || (c.ok === slot.check.ok && (c.ms ?? 0) > (slot.check.ms ?? 0))
    if (worse) slot.check = c
  }
  // Each beat sits at the middle of its checks, so the trace still reads left to right in time.
  return [...slots.entries()]
    .sort(([a], [b]) => a - b)
    .map(([, s]) => ({ check: { ...s.check, t: new Date(s.sumT / s.count).toISOString() }, count: s.count }))
}

function round1(n: number): number {
  return Math.round(n * 10) / 10
}

/** The response time the spikes scale against: the 95th percentile, at least 50 ms, so one slow outlier does not flatten the rest. */
export function pulseScale(checks: PulseCheck[]): number {
  const ms = checks.map((c) => c.ms).filter((v): v is number => v != null).sort((a, b) => a - b)
  if (ms.length === 0) return 50
  return Math.max(50, ms[Math.min(ms.length - 1, Math.floor(ms.length * 0.95))])
}
